import { useMemo, useState } from "react";
import { Database, Globe, Network, RefreshCw, ShieldAlert, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useApi } from "@/modules/collector_v2/hooks/useApi";
import { PageHeader } from "@/modules/collector_v2/components/common/PageHeader";
import { EmptyState } from "@/modules/collector_v2/components/common/EmptyState";
import { KpiCard } from "@/modules/collector_v2/components/common/KpiCard";

export default function NetworkGraphPage() {
  const [minCoPlay, setMinCoPlay] = useState(2);
  const [limitUids, setLimitUids] = useState(150);
  const [onlyVpn, setOnlyVpn] = useState(false);
  const [onlyOcean, setOnlyOcean] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);

  const query = useMemo(() => {
    const sp = new URLSearchParams({
      min_co_play: String(minCoPlay),
      limit_uids: String(limitUids),
      only_vpn: String(onlyVpn),
      only_ocean_gps: String(onlyOcean),
    });
    return `/v2/analytics/network-graph?${sp.toString()}`;
  }, [minCoPlay, limitUids, onlyVpn, onlyOcean]);

  const api = useApi(query);
  const data = api.data || { nodes: [], edges: [], summary: {}, clubs: [] };
  const { nodes, edges, summary, clubs } = data;

  // Layout: organizar nodos en círculo (simple deterministic)
  const layout = useMemo(() => {
    if (!nodes || nodes.length === 0) return { positions: {}, viewBox: "0 0 800 600" };
    const W = 1200;
    const H = 700;
    const cx = W / 2;
    const cy = H / 2;
    const radius = Math.min(W, H) * 0.42;
    const positions = {};
    // Agrupar por gps_country o "unknown"
    const groups = {};
    nodes.forEach((n) => {
      const k = n.is_ocean_gps ? "🚨 ocean" : (n.gps_country || n.country || "?");
      if (!groups[k]) groups[k] = [];
      groups[k].push(n);
    });
    const keys = Object.keys(groups).sort();
    let nodeIdx = 0;
    const totalN = nodes.length;
    keys.forEach((k, gIdx) => {
      const groupBase = (gIdx / keys.length) * 2 * Math.PI;
      const groupSize = groups[k].length;
      groups[k].forEach((n, i) => {
        // Distribuir el grupo en un arco
        const arcSize = (groupSize / totalN) * 2 * Math.PI * 0.92;
        const angle = groupBase + (i / Math.max(groupSize, 1)) * arcSize;
        const r = radius + (n.is_ocean_gps ? 30 : 0); // ocean nodes outward
        positions[n.id] = {
          x: cx + r * Math.cos(angle),
          y: cy + r * Math.sin(angle),
          group: k,
        };
        nodeIdx++;
      });
    });
    return { positions, viewBox: `0 0 ${W} ${H}` };
  }, [nodes]);

  const nodeColor = (n) => {
    if (n.is_ocean_gps) return "#dc2626"; // rojo: GPS bloqueado
    if (n.vpn_alert) return "#f59e0b"; // ámbar: VPN
    return "#3b82f6"; // azul: normal
  };

  const edgeColor = (e) => {
    const n1 = nodes.find((n) => n.id === e.source);
    const n2 = nodes.find((n) => n.id === e.target);
    if (!n1 || !n2) return "#94a3b8";
    if (n1.is_ocean_gps && n2.is_ocean_gps) return "#dc2626";
    if (n1.vpn_alert || n2.vpn_alert) return "#f59e0b";
    return "rgba(148, 163, 184, 0.4)";
  };

  return (
    <div className="page-content">
      <PageHeader
        eyebrow="Phase 6"
        title="Network Graph — Jugadores, GPS, Clubs"
        description="Visualiza la red de co-play. Nodos rojos = GPS bloqueado (≈0,0). Nodos ámbar = VPN/proxy. Aristas = manos compartidas entre uids."
        action={
          <Button data-testid="network-graph-refresh" variant="outline" onClick={api.refetch}>
            <RefreshCw className="h-4 w-4" /> Refrescar
          </Button>
        }
      />

      {/* KPIs */}
      <div className="kpi-grid">
        <KpiCard testid="network-kpi-nodes" icon={UsersRound} title="Jugadores" value={summary?.nodes_total || 0} description="Nodos en la red" />
        <KpiCard testid="network-kpi-edges" icon={Network} title="Co-play edges" value={summary?.edges_total || 0} description="Manos compartidas" />
        <KpiCard testid="network-kpi-vpn" icon={ShieldAlert} title="VPN nodes" value={summary?.vpn_nodes || 0} description="Profile ≠ GPS country" />
        <KpiCard testid="network-kpi-ocean" icon={Globe} title="GPS bloqueado" value={summary?.ocean_gps_nodes || 0} description="Coordenadas ≈ 0,0" />
        <KpiCard testid="network-kpi-clubs" icon={Database} title="Clubs distintos" value={summary?.clubs_total || 0} description="Afiliaciones" />
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Ajusta para enfocar la red.</CardDescription>
        </CardHeader>
        <CardContent className="actions-row">
          <div className="filter-block">
            <label className="filter-label">Min co-play hands</label>
            <Input data-testid="network-min-coplay" type="number" min={1} max={50} value={minCoPlay} onChange={(e) => setMinCoPlay(Math.max(1, Number(e.target.value) || 1))} className="small-filter-input" />
          </div>
          <div className="filter-block">
            <label className="filter-label">Limit UIDs</label>
            <Input data-testid="network-limit-uids" type="number" min={10} max={2000} value={limitUids} onChange={(e) => setLimitUids(Math.max(10, Number(e.target.value) || 10))} className="small-filter-input" />
          </div>
          <Button data-testid="network-filter-vpn" variant={onlyVpn ? "default" : "outline"} onClick={() => setOnlyVpn(!onlyVpn)}>
            <ShieldAlert className="h-4 w-4" /> {onlyVpn ? "VPN: ON" : "Solo VPN"}
          </Button>
          <Button data-testid="network-filter-ocean" variant={onlyOcean ? "default" : "outline"} onClick={() => setOnlyOcean(!onlyOcean)}>
            <Globe className="h-4 w-4" /> {onlyOcean ? "GPS bloqueado: ON" : "Solo GPS bloqueado"}
          </Button>
        </CardContent>
      </Card>

      {/* Grafo SVG */}
      <Card>
        <CardHeader>
          <CardTitle>Red de co-play</CardTitle>
          <CardDescription>Hover sobre un nodo para ver detalles.</CardDescription>
        </CardHeader>
        <CardContent>
          {api.loading && <Skeleton className="h-[600px] w-full" />}
          {!api.loading && nodes.length === 0 && (
            <EmptyState title="Sin datos para mostrar" description="Reduce min_co_play o quita los filtros." icon={Network} testid="network-empty-state" />
          )}
          {!api.loading && nodes.length > 0 && (
            <div data-testid="network-graph-svg-container" style={{ background: "rgba(15, 23, 42, 0.4)", borderRadius: 12, padding: 8, position: "relative" }}>
              <svg viewBox={layout.viewBox} style={{ width: "100%", height: 700 }}>
                {/* Edges first */}
                {edges.map((e, idx) => {
                  const p1 = layout.positions[e.source];
                  const p2 = layout.positions[e.target];
                  if (!p1 || !p2) return null;
                  return (
                    <line
                      key={`e-${idx}`}
                      x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                      stroke={edgeColor(e)}
                      strokeWidth={Math.min(4, 0.5 + e.co_play_hands / 30)}
                      opacity={0.6}
                    />
                  );
                })}
                {/* Nodes */}
                {nodes.map((n) => {
                  const p = layout.positions[n.id];
                  if (!p) return null;
                  const isHovered = hoveredNode?.id === n.id;
                  const r = isHovered ? 10 : (n.is_ocean_gps || n.vpn_alert ? 6 : 4);
                  return (
                    <g key={n.id} onMouseEnter={() => setHoveredNode(n)} onMouseLeave={() => setHoveredNode(null)} style={{ cursor: "pointer" }}>
                      <circle cx={p.x} cy={p.y} r={r} fill={nodeColor(n)} stroke="#0f172a" strokeWidth={1} />
                      {isHovered && (
                        <text x={p.x + 12} y={p.y - 4} fill="#fff" fontSize={11} fontWeight={600}>{n.nick || `#${n.uid}`}</text>
                      )}
                    </g>
                  );
                })}
              </svg>
              {/* Tooltip card */}
              {hoveredNode && (
                <div data-testid="network-node-tooltip" style={{ position: "absolute", bottom: 16, right: 16, background: "rgba(15, 23, 42, 0.95)", padding: 12, borderRadius: 8, color: "#fff", fontSize: 12, minWidth: 240, border: "1px solid #334155" }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{hoveredNode.nick || `UID ${hoveredNode.uid}`}</div>
                  <div>UID: {hoveredNode.uid}</div>
                  <div>Perfil país: <strong>{hoveredNode.country || "—"}</strong></div>
                  <div>GPS país: <strong>{hoveredNode.gps_country || "—"}</strong> {hoveredNode.city ? `(${hoveredNode.city})` : ""}</div>
                  {hoveredNode.gps_lat !== null && (
                    <div>GPS: {hoveredNode.gps_lat?.toFixed(4)}, {hoveredNode.gps_lon?.toFixed(4)}</div>
                  )}
                  <div>Club: {hoveredNode.club_name || "—"}</div>
                  {hoveredNode.vpn_alert && <div style={{ color: "#fbbf24", fontWeight: 600 }}>🚨 VPN/proxy</div>}
                  {hoveredNode.is_ocean_gps && <div style={{ color: "#f87171", fontWeight: 600 }}>🚨 GPS bloqueado</div>}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats por país y clubs top */}
      <div className="grid-2">
        <Card>
          <CardHeader><CardTitle>Top países (perfil vs GPS)</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Country</TableHead><TableHead>Profile</TableHead><TableHead>GPS</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const all = new Set([
                    ...((summary?.top_profile_countries || []).map((x) => x[0])),
                    ...((summary?.top_gps_countries || []).map((x) => x[0])),
                  ]);
                  const profMap = Object.fromEntries(summary?.top_profile_countries || []);
                  const gpsMap = Object.fromEntries(summary?.top_gps_countries || []);
                  return [...all].sort().map((cc) => (
                    <TableRow key={cc} data-testid={`network-country-row-${cc}`}>
                      <TableCell>{cc}</TableCell>
                      <TableCell>{profMap[cc] || 0}</TableCell>
                      <TableCell>{gpsMap[cc] || 0}</TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Top clubs</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Club</TableHead><TableHead>UIDs</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {(clubs || []).slice(0, 15).map((c) => (
                  <TableRow key={c.club_id} data-testid={`network-club-row-${c.club_id}`}>
                    <TableCell>{c.club_name || `Club ${c.club_id}`}</TableCell>
                    <TableCell>{c.uids_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
